<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-table-header">
          <div class="title-container">
            <h3 class="title">
              Cards
              <div
                class="spinner-border text-primary"
                role="status"
                v-if="isDataPageLoading"
              >
                <span class="sr-only">
                  Loading...
                </span>
              </div>
            </h3>
            <b-button
              v-if="$can(permissions.CreateFinancesOwnBanDetails)"
              class="button"
              variant="success"
              @click.prevent="showCreateBankDetailsCards"
              :disabled="isDataPageLoading"
            >
              Create
            </b-button>
          </div>
          <TableForPage
            v-if="getBankDetailsCardsTableData.length"
            ref="table"
            :tableData="getBankDetailsCardsTableData"
            :fields="table.fields"
            :isEdit="$can(permissions.UpdateAllFinancesOwnBankDetails)"
            :isRemove="$can(permissions.DeleteFinancesOwnBankDetails)"
            @edit="showEditBankDetailsCardsModal"
            @remove="removeBankDetailsCardsModal"
          />
          <div class="table__empty" v-else>
            Table is empty
          </div>
        </div>
      </div>
    </div>

    <b-modal
      size="lg"
      hide-footer
      v-model="createBankDetailsCards.show"
      class="modal"
    >
      <template v-slot:modal-header="{ cancel }">
        <h5 class="modal-title">{{createBankDetailsCards.title}}</h5>
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click.prevent="cancel"
        >
          <span class="material-icons">
            close
          </span>
        </button>
      </template>
      <FormGenerator
        :data="createBankDetailsCards.form"
        :isLoading="isDataPageLoading"
        @submit="createFinanceBankDetailsCards"
      />
    </b-modal>
    <b-modal
      size="lg"
      hide-footer
      v-model="editBankDetailsCards.show"
      :title="editBankDetailsCards.title"
      class="modal"
    >
      <FormGenerator
        :data="editBankDetailsCards.form"
        :submitButtonText="editBankDetailsCards.submitButtonText"
        @submit="editFinanceBankDetailsCards"
      />
    </b-modal>

  </div>
</template>

<script>
import { permissions } from '@mocks/permissions'
import TableForPage from '@tables/TableForPage'
import FormGenerator from '@components/FormGenerator'
import { mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'

const formMock = {
  'id': {
    name: 'id'
  },
  'bank_details': {
    type: 'select',
    name: 'bank_details_id',
    label: 'Bank Details',
    selectLabel: 'summary',
    valueField: 'id',
    options: [],
    validation: [
      { name: 'required' }
    ],
    class: 'col-12'
  },
  'expired_at': {
    type: 'datepicker',
    name: 'expired_at',
    label: 'Expired',
    placeholder: 'Example: 2020-12-12',
    format: 'yyyy-MM-dd',
    validation: [
      { name: 'required' }
    ],
    value: null,
    class: 'col-12'
  },
  'name': {
    type: 'input',
    name: 'name',
    label: 'Name',
    placeholder: 'name',
    validation: [
      { name: 'required' }
    ],
    value: null,
    class: 'col-12'
  },
  'number': {
    type: 'input',
    name: 'number',
    label: 'Number',
    placeholder: 'Number',
    validation: [
      { name: 'required' }
    ],
    value: null,
    class: 'col-12'
  }
}

export default {
  name: 'FinanceBankDetailsCards',
  components: { TableForPage, FormGenerator },
  data () {
    return {
      isShowFilter: true,
      permissions: permissions,
      createBankDetailsCards: {
        show: false,
        title: 'Create card',
        form: cloneDeep(formMock)
      },
      editBankDetailsCards: {
        show: false,
        title: 'Edit card',
        submitButtonText: 'Edit',
        form: cloneDeep(formMock)
      },
      table: {
        fields: [
          {
            key: 'number',
            label: 'Number',
            sortable: true
          },
          {
            key: 'name',
            label: 'Name',
            sortable: true
          },
          {
            key: 'expired_at',
            label: 'Expired ',
            sortable: true
          },
          {
            key: 'bank_details.summary',
            label: 'Summary ',
            sortable: true
          },
          {
            key: 'actions',
            label: 'Actions',
            class: 'column_action'
          }
        ]
      },
      isDataPageLoading: true
    }
  },
  created () {
    this.fetchAllData()
  },
  computed: {
    ...mapGetters({
      getBankDetailsCards: 'finances/getBankDetailsCards',
      getOwnBankDetails: 'finances/getOwnBankDetails'
    }),
    getBankDetailsCardsTableData () {
      return this.getBankDetailsCards || []
    }
  },
  methods: {
    async fetchAllData () {
      this.fetchBankDetailsCards()
      await this.fetchAllBankDetails()
      this.setAllOptionsFilter()
    },
    async fetchAllBankDetails () {
      return this.$api.finances.getBankDetailsAll()
    },
    showCreateBankDetailsCards () {
      this.createBankDetailsCards.show = true
    },
    hideCreateBankDetailsCardsModal () {
      this.createBankDetailsCards.show = false
    },
    showEditBankDetailsCardsModal (params) {
      this.editBankDetailsCards.show = true
      this.setParamsForEditBankDetailsCardsModal(params)
    },
    hideEditBankDetailsCardsModal () {
      this.editBankDetailsCards.show = false
    },
    setParamsForEditBankDetailsCardsModal (params) {
      let value = ''

      for (let key in this.editBankDetailsCards.form) {
        if (params[key]) {
          value = params[key]
        } else {
          value = ''
        }

        this.editBankDetailsCards.form[key]['value'] = value
      }
    },
    setAllOptionsFilter () {
      this.createBankDetailsCards.form.bank_details.options = this.getOwnBankDetails
      this.editBankDetailsCards.form.bank_details.options = this.getOwnBankDetails
    },

    fetchBankDetailsCards () {
      this._toggleDataPageLoading(() => this.$api.finances.getBankDetailsCards())
    },
    prepareDataForEditAndCreateRequest (fields) {
      let data = {}

      for (let key in fields) {
        const field = fields[key]
        const name = field['name']
        const value = field['value']

        if (key === 'expired_at') {
          data[name] = new Date(value).toJSON().slice(0, 10)
          continue
        }

        data[name] = typeof value === 'object' ? value[field['valueField']] : value
      }

      return data
    },
    editFinanceBankDetailsCards (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)

      this._toggleDataPageLoading(async () => {
        await this.$api.finances.editBankDetailsCards(data)
        this.hideEditBankDetailsCardsModal()
      })
    },

    async removeBankDetailsCardsModal (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the bank details card?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        await this.$api.finances.removeBankDetailsCards(item.id)
      })
    },
    async createFinanceBankDetailsCards (fields) {
      const data = this.prepareDataForEditAndCreateRequest(fields)

      this._toggleDataPageLoading(async () => {
        await this.$api.finances.createBankDetailsCards(data)
        this.hideCreateBankDetailsCardsModal()
      })
    }
  }
}
</script>
